import {
	ASTEROID_RADIUS,
	ASTEROID_SIZE_SCALE,
	ASTEROID_SPLIT_KICK,
	OBJECTIVE_RESPAWN_TICKS,
	RESPAWN_DELAY_TICKS,
} from "../constants";
import {
	type Asteroid,
	createAsteroid,
	type Destroyable,
	type Entity,
	isProjectile,
	type PlayerIndex,
} from "../entities";
import type { SimEvent } from "../events";
import * as DMath from "../math/det-math";
import type { WorldState } from "../state";
import { allocId, insertEntity, queueRemoval } from "../world";

/**
 * Destroys a destroyable (ship, asteroid, missile): queues its removal, updates player
 * bookkeeping and stats (legacy Player::notifyEvent(WorldObjectDestructionEvent)) and emits
 * a `destroyed` event. `destroyed` tracks ids already destroyed this tick so an entity is
 * only destroyed once (legacy could double-count; see CLAUDE.md "deliberate deviations").
 */
export function destroyEntity(
	world: WorldState,
	target: Destroyable,
	cause: Entity,
	events: SimEvent[],
	destroyed: Set<number>,
): void {
	if (destroyed.has(target.id)) return;
	destroyed.add(target.id);
	queueRemoval(world, target.id);

	// The victim's owner loses its ship and starts the respawn countdown.
	if (target.kind === "ship") {
		const owner = world.players[target.player];
		if (owner && owner.shipId === target.id) {
			owner.shipId = null;
			// Objective modes: a longer wait, so a kill opens a window to take the hill or the flag.
			// (A score-limit duel keeps the legacy respawn: it is a duel with a different end.)
			const objective = world.objective;
			owner.respawnCountdown =
				objective && objective.kind !== "frags" ? OBJECTIVE_RESPAWN_TICKS : RESPAWN_DELAY_TICKS;
		}
	}

	const creditedPlayer = creditFor(world, target, cause);
	if (creditedPlayer !== null) {
		const shooter = world.players[creditedPlayer];
		if (shooter && target.kind === "ship") shooter.shipKills++;
		if (shooter && target.kind === "asteroid") shooter.asteroidKills++;
	}

	events.push({
		type: "destroyed",
		id: target.id,
		kind: target.kind,
		pos: { x: target.pos.x, y: target.pos.y },
		angle: target.angle,
		cause: { id: cause.id, kind: cause.kind, pos: { x: cause.pos.x, y: cause.pos.y } },
		creditedPlayer,
	});

	// Growing planets (P11.7): an asteroid crashing into a planet feeds it, up to double its mass.
	const growth = world.rules.planetGrowth;
	if (target.kind === "asteroid" && cause.kind === "planet" && growth !== null) {
		cause.mass = Math.min(cause.baseMass * 2, cause.mass + growth);
	}

	if (
		target.kind === "asteroid" &&
		cause.kind === "laser" &&
		world.rules.asteroidSplitting &&
		target.size < ASTEROID_SIZE_SCALE.length - 1
	) {
		splitAsteroid(world, target, cause, events);
	}
}

/**
 * Asteroid splitting rule (P11.6, 2008 req. 6.2.22): two smaller asteroids fly apart across
 * the laser's line, keeping the parent's velocity plus a sideways kick, spinning opposite ways.
 * They join the world now (appended, so id order holds) and collide from the next tick.
 */
function splitAsteroid(
	world: WorldState,
	parent: Asteroid,
	laser: Entity,
	events: SimEvent[],
): void {
	const dx = laser.vel.x;
	const dy = laser.vel.y;
	const len = DMath.hypot(dx, dy) || 1;
	// Unit vector across the shot.
	const nx = -dy / len;
	const ny = dx / len;
	const size = parent.size + 1;
	const offset = ASTEROID_RADIUS * (ASTEROID_SIZE_SCALE[size] ?? 1);
	for (const side of [-1, 1]) {
		const child = createAsteroid(
			allocId(world),
			{ x: parent.pos.x + nx * offset * side, y: parent.pos.y + ny * offset * side },
			{
				x: parent.vel.x + nx * ASTEROID_SPLIT_KICK * side,
				y: parent.vel.y + ny * ASTEROID_SPLIT_KICK * side,
			},
			parent.spin * -side,
			size,
		);
		insertEntity(world, child, events);
	}
}

/** The projectile's owner gets credit, except for destroying their own ship. */
function creditFor(world: WorldState, target: Destroyable, cause: Entity): PlayerIndex | null {
	if (!isProjectile(cause)) return null;
	if (target.kind === "ship" && target.player === cause.owner) return null;
	return world.players[cause.owner] ? cause.owner : null;
}
